import {
  EMAIL_NOTIFICATION,
  LOCATION_SELECT_ALL,
  NOTIFICATION_FEATURE_LABEL,
  NOTIFICATION_KIND,
  NOTIFICATION_KIND_LABEL,
  NOTIFICATION_FORM_COPY,
  NOTIFICATION_TYPE_KEYS,
} from './constants.js';
import { isNotificationEnabled } from './notificationRecordUtils.js';
import {
  describeSchedule,
  formatNextRunAt,
  scheduleFromLegacyCron,
} from './notificationScheduleUtils.js';

const NAME_MAX = 120;
export const NOTIFICATION_NAME_ALL_LOCATIONS = 'All Locations';
export const NOTIFICATION_NAME_MULTIPLE_LOCATIONS = 'Multiple locations';

export function featureDisplayName(feature) {
  return NOTIFICATION_FEATURE_LABEL[feature] || feature || 'Notification';
}

function normalizeLocationIdList(value) {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.flatMap(normalizeLocationIdList);
  if (typeof value === 'object' && value._id) return [String(value._id)];
  const raw = String(value).trim();
  if (!raw) return [];
  if (raw.includes(',')) {
    return raw.split(',').map((part) => part.trim()).filter(Boolean);
  }
  return [raw];
}

export function locationIdsFromMeta(meta = {}) {
  const combined = [
    ...normalizeLocationIdList(meta.locations),
    ...normalizeLocationIdList(meta.location_key),
  ];
  const seen = new Set();
  return combined.filter((id) => {
    const key = String(id);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function resolveLocationNames(ids = [], lookups = []) {
  if (!ids.length) return [NOTIFICATION_NAME_ALL_LOCATIONS];
  return ids.map((id) => {
    const loc = (lookups || []).find(
      (row) => String(row._id) === String(id) || String(row.id) === String(id)
    );
    return loc?.name || String(id);
  });
}

export function recipientListFromMeta(meta = {}) {
  const list = meta.toUsers || meta.emails || [];
  return Array.isArray(list) ? list : [];
}

/** String label for filters (matches NotificationRecipientChips tooltip text). */
export function recipientDisplayLabel(entry) {
  if (entry == null || entry === '') return '';
  if (typeof entry === 'object') {
    return String(entry.name || entry.displayname || entry.email || entry.contactemail || '').trim();
  }
  return String(entry).trim();
}

function recipientFilterLabels(meta, roleLabels) {
  const toUsers = recipientListFromMeta(meta);
  const source = toUsers.length ? toUsers : roleLabels;
  return source.map(recipientDisplayLabel).filter(Boolean);
}

function durationDays(item = {}) {
  const days = item.meta?.endDays ?? item.end_days;
  if (days == null || days === '') return null;
  const n = Number(days);
  return Number.isFinite(n) ? n : null;
}

function parseAbsoluteDate(value) {
  if (value == null || value === '' || typeof value === 'number') return null;
  const raw = String(value).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  const date = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function absoluteEndDateLabel(value) {
  const date = parseAbsoluteDate(value);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Cycle digest Active until: calendar date, never the template "days after" wording. */
export function cycleEndDateLabel(item = {}) {
  const raw = item.meta?.endDate ?? item.endDate ?? item.end ?? item.meta?.end;
  return absoluteEndDateLabel(raw) || '—';
}

export function activeWindowParts(item = {}) {
  const days = durationDays(item);
  if (days != null) {
    const dayLabel = days === 1 ? '1 day' : `${days} days`;
    return { primary: `${dayLabel} after period end`, sub: '' };
  }
  const dateLabel = absoluteEndDateLabel(item.meta?.endDate ?? item.endDate);
  if (dateLabel) {
    return { primary: dateLabel, sub: '' };
  }
  return null;
}

export function activeWindowLabel(item = {}) {
  const parts = activeWindowParts(item);
  if (!parts) return '—';
  return [parts.primary, parts.sub].filter(Boolean).join(' ');
}

function ensureIdArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
}

export function escalationAppliesWhenLines(item = {}, categoryLookups = []) {
  const meta = item.meta || {};
  const categoryIds = ensureIdArray(meta.category);
  if (categoryIds.length) {
    const id = categoryIds[0];
    const match = (categoryLookups || []).find(
      (row) => String(row.id) === String(id) || String(row._id) === String(id)
    );
    const name = match?.name || String(id);
    return { kind: 'category', value: name, sub: 'and task is past due' };
  }
  const priority = meta.priority || item.priority || 'High';
  return { kind: 'priority', value: priority, sub: 'and task is past due' };
}

export function notificationTypeSummary(item = {}) {
  const feature = item.feature;
  const meta = item.meta || {};
  if (feature === NOTIFICATION_TYPE_KEYS.TASK_STATUS_REMINDER) {
    const signed = meta.taskDueDateOffsetWorkingDays ?? meta.offset ?? meta.dueOffset;
    const { magnitude, direction } = fromSignedOffset(signed);
    if (!magnitude) return 'Due today and overdue only';
    const when = direction === 'after' ? 'after' : 'before';
    return `${magnitude} working days ${when} due`;
  }
  if (feature === NOTIFICATION_TYPE_KEYS.UPCOMING_DUE_BY_LOCATION) {
    const signed = meta.earliestDueDateOffsetWorkingDays ?? meta.offset;
    const { magnitude, direction } = fromSignedOffset(signed);
    if (!magnitude) return 'Before earliest due date';
    const when = direction === 'after' ? 'after' : 'before';
    return `${magnitude} working days ${when} earliest due`;
  }
  if (feature === NOTIFICATION_TYPE_KEYS.RECON_STATUS_CHANGE) {
    return 'Matched → Unmatched';
  }
  if (feature === NOTIFICATION_TYPE_KEYS.TASK_REJECTED) {
    return 'Approver sends a task back';
  }
  return '';
}

function filterSelectableLocationIds(ids = []) {
  return (ids || []).filter(
    (id) => String(id) !== LOCATION_SELECT_ALL.ID && String(id) !== 'all',
  );
}

export function isAllLocationsSelection(ids = [], lookups = []) {
  const selected = filterSelectableLocationIds(ids);
  if (!selected.length) return true;
  if (selected.some((id) => String(id) === LOCATION_SELECT_ALL.ID || String(id) === 'all')) {
    return true;
  }
  const lookupIds = (lookups || [])
    .map((loc) => loc._id ?? loc.id)
    .filter((id) => id != null && id !== '')
    .map(String);
  if (!lookupIds.length) return false;
  const selectedSet = new Set(selected.map(String));
  return lookupIds.every((id) => selectedSet.has(id));
}

export function locationNameSuffix(locationNames = [], selectedCount = 0) {
  const names = (locationNames || []).filter(Boolean);
  const count = names.length || selectedCount;
  if (!count) return NOTIFICATION_NAME_ALL_LOCATIONS;
  if (count > 2) return NOTIFICATION_NAME_MULTIPLE_LOCATIONS;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  return NOTIFICATION_NAME_MULTIPLE_LOCATIONS;
}

/**
 * Default name: "<Type label> — <Locations>". Task rejected has no location suffix.
 */
export function buildNotificationName(feature, meta = {}, lookups = []) {
  const label = featureDisplayName(feature);
  if (feature === NOTIFICATION_TYPE_KEYS.TASK_REJECTED) {
    return String(label).slice(0, NAME_MAX);
  }
  const ids = filterSelectableLocationIds(locationIdsFromMeta(meta));
  if (isAllLocationsSelection(ids, lookups)) {
    return `${label} — ${NOTIFICATION_NAME_ALL_LOCATIONS}`.slice(0, NAME_MAX);
  }
  const names = ids.map((id) => {
    const match = (lookups || []).find(
      (loc) => String(loc._id) === String(id) || String(loc.id) === String(id),
    );
    return match?.name ? String(match.name).trim() : '';
  });
  let suffix;
  if (ids.length > 2) {
    suffix = NOTIFICATION_NAME_MULTIPLE_LOCATIONS;
  } else if (ids.length === 2) {
    suffix = names[0] && names[1]
      ? `${names[0]}, ${names[1]}`
      : NOTIFICATION_NAME_MULTIPLE_LOCATIONS;
  } else if (ids.length === 1) {
    suffix = names[0] || NOTIFICATION_NAME_ALL_LOCATIONS;
  } else {
    suffix = NOTIFICATION_NAME_ALL_LOCATIONS;
  }
  let name = `${label} — ${suffix}`;
  if (name.length <= NAME_MAX) return name;
  const room = NAME_MAX - label.length - 3;
  return `${label} — ${suffix.slice(0, Math.max(0, room))}`;
}

export const OFFSET_MAGNITUDE_MAX = 200;

/**
 * Working-day magnitude only. The Before/After control owns the sign.
 * A leading minus is rejected so it is not folded into a positive number.
 * @param {unknown} raw
 * @returns {{ magnitude: number|null, error: string }}
 */
export function parseOffsetMagnitude(raw) {
  const text = raw == null ? '' : String(raw).trim();
  if (text === '') {
    return { magnitude: null, error: NOTIFICATION_FORM_COPY.OFFSET_REQUIRED };
  }
  if (!/^\d+$/.test(text)) {
    return { magnitude: null, error: NOTIFICATION_FORM_COPY.OFFSET_WHOLE_NUMBER };
  }
  const magnitude = Number(text);
  if (magnitude > OFFSET_MAGNITUDE_MAX) {
    return { magnitude: null, error: NOTIFICATION_FORM_COPY.OFFSET_WHOLE_NUMBER };
  }
  return { magnitude, error: '' };
}

export function toSignedOffset(magnitude, direction) {
  const parsed = parseOffsetMagnitude(magnitude);
  const n = parsed.error ? 0 : parsed.magnitude;
  return direction === 'after' ? n : -n;
}

export function fromSignedOffset(value) {
  const n = Number(value) || 0;
  if (n > 0) return { magnitude: n, direction: 'after' };
  return { magnitude: Math.abs(n), direction: 'before' };
}

/** Display value for Next run column / filters (matches ActiveSchedulesTable). */
export function notificationNextRunDisplay(item = {}) {
  if (!isNotificationEnabled(item)) return '—';
  if (item.kind === NOTIFICATION_KIND.TRIGGER) return 'When it happens';
  return formatNextRunAt(item.nextRunAt) || '—';
}

/** Automation name from DB only (run script 18 to backfill legacy rows). */
export function storedNotificationName(item = {}) {
  return String(item?.name ?? '').trim();
}

/**
 * Flat fields on each row for lb-datatableaction column filters (header `value` keys).
 * @param {object} item
 * @param {{ locations?: object[] }} [options]
 */
export function notificationTableFilterRow(item = {}, { locations = [] } = {}) {
  const roles = item.meta?.roles || [];
  const roleLabels = (EMAIL_NOTIFICATION.roles || [])
    .filter((role) => roles.includes(role.value))
    .map((role) => role.name);
  const locationNames = resolveLocationNames(locationIdsFromMeta(item.meta), locations);
  const enabled = isNotificationEnabled(item);
  const scheduleMeta = item.meta?.schedule;
  const resolvedSchedule = (scheduleMeta && scheduleMeta.startTime)
    ? scheduleMeta
    : (item.schedulerTiming ? scheduleFromLegacyCron(item.schedulerTiming) : null);
  const sendsAt = resolvedSchedule ? describeSchedule(resolvedSchedule) : '';

  return {
    ...item,
    name: storedNotificationName(item),
    type: NOTIFICATION_FEATURE_LABEL[item.feature] || item.feature || '',
    trigger: NOTIFICATION_KIND_LABEL[item.kind] || item.kind || '',
    subject: item.subject || '',
    locations: locationNames,
    recipients: recipientFilterLabels(item.meta, roleLabels),
    lastRun: formatNextRunAt(item.lastRun || item.lastRunSummary?.at) || '',
    nextRun: notificationNextRunDisplay(item),
    sendsAt,
    checksAt: sendsAt,
    activeWindow: activeWindowLabel(item),
    status: enabled ? 'On' : 'Off',
  };
}

export function createdByLine(doc) {
  const created = doc?.created_by;
  const name = created && typeof created === 'object' ? created.name : '';
  const at = doc?.created_at ? new Date(doc.created_at) : null;
  const dateLabel = at && !Number.isNaN(at.getTime())
    ? at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : '';
  if (name && dateLabel) return `Created by ${name} · ${dateLabel}`;
  if (name) return `Created by ${name}`;
  if (dateLabel) return dateLabel;
  return '';
}
